import { ApiError, type ThreadDetail, type ThreadKind, type ThreadPriority } from '@central/api';
import { charCount } from '@central/ui';
import { Modal, Select, TextInput, Textarea } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { keys, useApi } from '../api';
import { CustomerSelect } from '../components/CustomerSelect';
import { environmentOptions, useCustomerEnvironments } from '../lib/environment';
import { errorMessage } from '../lib/errors';
import { priorities, priorityChip } from '../lib/inbox';
import { useIsReseller } from '../lib/me';
import { Form } from './CustomerDialogs';

/** Kinds staff may open a thread as; crash threads come from crash reports only. */
const kinds: Array<{ value: Exclude<ThreadKind, 'crash'>; label: string }> = [
  { value: 'question', label: 'Question' },
  { value: 'renewal', label: 'Renewal' },
  { value: 'license', label: 'License change' },
  { value: 'bug', label: 'Bug' },
  { value: 'feature', label: 'Feature' },
];

/** A reseller starts support threads only; licensing goes through Rethink. */
const resellerKinds = kinds.filter((k) => k.value !== 'renewal' && k.value !== 'license');

const MAX_SUBJECT = 200;
const MAX_BODY = 20_000;

interface Draft {
  customerId: string;
  environmentId: string;
  kind: Exclude<ThreadKind, 'crash'>;
  priority: ThreadPriority;
  subject: string;
  body: string;
}

type Problems = Partial<Record<keyof Draft, string>>;

function check(d: Draft): Problems {
  const p: Problems = {};
  if (!d.customerId) p.customerId = 'Choose a customer.';
  const subject = d.subject.trim();
  if (!subject) p.subject = 'Write a subject.';
  else if (charCount(subject) > MAX_SUBJECT) p.subject = 'Keep it under 200 characters.';
  const body = d.body.trim();
  if (!body) p.body = 'Write the message.';
  else if (charCount(body) > MAX_BODY) p.body = 'Keep it under 20,000 characters.';
  return p;
}

const serverFields = (e: unknown): Record<string, string> =>
  e instanceof ApiError ? (e.fields ?? {}) : {};

/**
 * "Message a customer" (docs/design/AdminInbox.dc.html): a staff-started
 * thread, optionally about one of the customer's environments. The customer
 * sees it unread, as "From Rethink". `onSent` gets the new thread.
 */
export function MessageCustomerModal({
  opened,
  onClose,
  onSent,
  customerId = '',
}: {
  opened: boolean;
  onClose: () => void;
  onSent: (d: ThreadDetail) => void;
  /** Preselected customer, e.g. the inbox's customer filter. */
  customerId?: string;
}) {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Message a customer"
      size="lg"
      closeButtonProps={{ 'aria-label': 'Close' }}
    >
      {/* Mounted per opening, so a closed dialog leaves no draft behind. */}
      {opened && <MessageForm initialCustomer={customerId} onClose={onClose} onSent={onSent} />}
    </Modal>
  );
}

function MessageForm({
  initialCustomer,
  onClose,
  onSent,
}: {
  initialCustomer: string;
  onClose: () => void;
  onSent: (d: ThreadDetail) => void;
}) {
  const api = useApi();
  const qc = useQueryClient();
  const reseller = useIsReseller();
  const [d, setD] = useState<Draft>({
    customerId: initialCustomer,
    environmentId: '',
    kind: 'question',
    priority: 'normal',
    subject: '',
    body: '',
  });
  const [problems, setProblems] = useState<Problems>({});
  const envs = useCustomerEnvironments(d.customerId);
  const send = useMutation({
    mutationFn: () =>
      (reseller ? api.partner.threads : api.threads).create({
        customerId: d.customerId,
        kind: d.kind,
        priority: d.priority,
        subject: d.subject.trim(),
        body: d.body.trim(),
        environmentId: d.environmentId || undefined,
      }),
    onSuccess: async (created) => {
      await qc.invalidateQueries({ queryKey: keys.threads });
      onSent(created);
    },
  });
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((p) => ({ ...p, [k]: v }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const p = check(d);
    setProblems(p);
    if (Object.keys(p).length === 0 && !send.isPending) send.mutate();
  };
  const server = serverFields(send.error);
  const fieldError = (k: keyof Draft, text: string) =>
    problems[k] ?? (server[k] ? text : undefined);
  const otherError = send.error && Object.keys(server).length === 0 ? send.error : undefined;

  return (
    <Form
      onSubmit={submit}
      error={otherError}
      submitLabel="Send"
      pending={send.isPending}
      onCancel={onClose}
    >
      <CustomerSelect
        label="Customer"
        placeholder="Choose a customer"
        value={d.customerId}
        onChange={(id) => setD((p) => ({ ...p, customerId: id, environmentId: '' }))}
        error={fieldError('customerId', 'This customer no longer exists.')}
      />
      <Select
        label="Environment"
        description="Optional: the environment the message is about"
        placeholder={d.customerId ? 'Not about one environment' : 'Choose a customer first'}
        disabled={!d.customerId}
        clearable
        data={environmentOptions(envs.data)}
        value={d.environmentId || null}
        onChange={(v) => set('environmentId', v ?? '')}
        error={
          fieldError('environmentId', 'That environment is not this customer’s.') ??
          (envs.error ? errorMessage(envs.error) : undefined)
        }
      />
      <Select
        label="Kind"
        data={reseller ? resellerKinds : kinds}
        value={d.kind}
        allowDeselect={false}
        onChange={(v) => v && set('kind', v as Draft['kind'])}
      />
      <Select
        label="Priority"
        data={priorities.map((p) => ({ value: p, label: priorityChip[p].label }))}
        value={d.priority}
        allowDeselect={false}
        onChange={(v) => v && set('priority', v as ThreadPriority)}
      />
      <TextInput
        label="Subject"
        value={d.subject}
        onChange={(e) => set('subject', e.currentTarget.value)}
        error={fieldError('subject', 'Check the subject.')}
      />
      <Textarea
        label="Message"
        autosize
        minRows={4}
        value={d.body}
        onChange={(e) => set('body', e.currentTarget.value)}
        error={fieldError('body', 'Check the message.')}
      />
    </Form>
  );
}
